<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { showFailToast } from "vant";

import { api, apiMessage } from "../api";

interface RankedItem {
  code: string;
  name: string;
  score: number;
}

interface Summary {
  validity: { score: number; level: string; details: { explanation: string } };
  mbti_reference: {
    status: "available" | "unavailable";
    type_code: string | null;
    summary: string;
    disclaimer: string;
    consistency_score: number | null;
    consistency_label: string;
    axes: Array<{
      code: string;
      name: string;
      left_letter: string;
      right_letter: string;
      right_score: number;
      preferred_letter: string | null;
      preferred: string | null;
      clarity_label: string;
      confidence_label?: string;
      verification_outcome?: string;
    }>;
  };
  style: { type_code: string; axes: Array<{ name: string; preferred: string }> };
  top_talents: RankedItem[];
  top_values: RankedItem[];
  top_abilities: RankedItem[];
  description: string;
  disclaimer: string;
  report_message: string;
}

const route = useRoute();
const summary = ref<Summary | null>(null);

onMounted(async () => {
  try {
    const { data } = await api.get(`/public/sessions/${String(route.params.token)}/summary`);
    summary.value = data;
  } catch (error) {
    showFailToast(apiMessage(error));
  }
});
</script>

<template>
  <main class="summary-page">
    <van-skeleton v-if="!summary" title :row="10" />
    <template v-else>
      <section class="summary-hero">
        <div class="section-kicker">测评已完成</div>
        <h1>你的职业发展速览</h1>
        <p>{{ summary.description }}</p>
        <div class="validity-badge">
          <strong>{{ summary.validity.score }}</strong>
          <span>作答过程质量<br />{{ summary.validity.level }}</span>
        </div>
      </section>

      <section v-if="summary.mbti_reference.status === 'available'" class="result-card mbti-card">
        <span class="section-kicker">MBTI 框架职业性格参考</span>
        <h2>{{ summary.mbti_reference.type_code }}</h2>
        <p>{{ summary.mbti_reference.summary }}</p>
        <div class="mbti-quality">
          四轴平行题一致性 {{ summary.mbti_reference.consistency_score }} ·
          {{ summary.mbti_reference.consistency_label }}
        </div>
        <div class="mbti-axes">
          <div v-for="axis in summary.mbti_reference.axes" :key="axis.code" class="mbti-axis">
            <div class="mbti-axis-head">
              <b>{{ axis.left_letter }}</b>
              <span>{{ axis.name }} · {{ axis.preferred }} · {{ axis.clarity_label }} · 置信度 {{ axis.confidence_label || "未分层" }}</span>
              <b>{{ axis.right_letter }}</b>
            </div>
            <div class="mbti-axis-track">
              <i :style="{ left: `${axis.right_score}%` }"></i>
            </div>
          </div>
        </div>
        <small>{{ summary.mbti_reference.disclaimer }}</small>
      </section>

      <section class="result-card">
        <span class="section-kicker">CareerLens 职业风格</span>
        <h2>{{ summary.style.type_code }}</h2>
        <div class="tag-list">
          <span v-for="axis in summary.style.axes" :key="axis.name">{{ axis.preferred }}</span>
        </div>
      </section>

      <section class="result-card">
        <span class="section-kicker">优先价值</span>
        <div class="rank-list">
          <div v-for="(item, index) in summary.top_values" :key="item.code">
            <b>0{{ index + 1 }}</b><span>{{ item.name }}</span><em>{{ item.score }}</em>
          </div>
        </div>
      </section>

      <section class="result-card">
        <span class="section-kicker">前五才干</span>
        <div class="rank-list">
          <div v-for="(item, index) in summary.top_talents" :key="item.code">
            <b>{{ String(index + 1).padStart(2, "0") }}</b><span>{{ item.name }}</span><em>{{ item.score }}</em>
          </div>
        </div>
      </section>

      <section class="result-card">
        <span class="section-kicker">优势能力</span>
        <div class="tag-list">
          <span v-for="item in summary.top_abilities" :key="item.code">{{ item.name }}</span>
        </div>
      </section>

      <section class="report-contact">
        <h2>需要完整报告？</h2>
        <p>{{ summary.report_message }}</p>
      </section>
      <p class="disclaimer">{{ summary.disclaimer }}</p>
    </template>
  </main>
</template>
